'use client'

import { useState, useMemo } from 'react'
import { MessageSquarePlus, Send, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

type FeedbackType = 'bug' | 'improvement' | 'feature'

const TYPE_ORDER: FeedbackType[] = ['bug', 'improvement', 'feature']

export function FeedbackWidget() {
  const t = useTranslations('Feedback')
  const [open, setOpen] = useState(false)
  const [type, setType] = useState<FeedbackType>('bug')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const [error, setError] = useState('')

  const disableSubmit = useMemo(
    () => !description.trim() || status === 'loading',
    [description, status],
  )

  const resetForm = () => {
    setType('bug')
    setDescription('')
    setEmail('')
    setStatus('idle')
    setError('')
  }

  const close = () => {
    resetForm()
    setOpen(false)
  }

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError(t('required'))
      return
    }

    setStatus('loading')
    setError('')

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          description,
          email: email.trim() || undefined,
          page:
            typeof window !== 'undefined' ? window.location.href : undefined,
        }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.message || 'Submit failed')
      }

      setStatus('success')
      setTimeout(() => close(), 900)
    } catch (e: any) {
      setStatus('error')
      setError(e?.message || t('error'))
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="relative"
        aria-label={t('trigger')}
      >
        <MessageSquarePlus className="h-5 w-5" />
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/45 px-4 py-8 backdrop-blur dark:bg-black/60 sm:items-center"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card/95 text-foreground shadow-[0_22px_70px_-30px_rgba(0,0,0,0.6)] backdrop-blur"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-border/80 px-5 py-4 sm:px-6">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold tracking-tight text-foreground">
                  {t('title')}
                </h2>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {t('subtitle')}
                </p>
              </div>
              <button
                aria-label={t('cancel')}
                onClick={close}
                className="rounded-full p-2 text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-5 px-5 pb-6 pt-4 sm:px-6">
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {t('typeLabel')}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {TYPE_ORDER.map((item) => (
                    <button
                      key={item}
                      onClick={() => setType(item)}
                      className={cn(
                        'group relative overflow-hidden rounded-lg border border-foreground/15 px-3 py-2 text-xs font-semibold uppercase tracking-[0.08em] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                        type === item
                          ? 'bg-foreground text-background shadow-[0_10px_30px_-15px_rgba(0,0,0,0.55)]'
                          : 'bg-muted text-foreground/80 hover:border-foreground/30 hover:text-foreground',
                      )}
                    >
                      <span className="relative z-10">
                        {t(`types.${item}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <p className="text-sm font-medium text-foreground">
                    <span className="text-red-600">*</span>
                    {t('descriptionLabel')}
                  </p>
                  {/* <span className="rounded-full border border-foreground/10 px-2 py-[2px] text-[10px] font-medium text-foreground/70">
                    {t('required')}
                  </span> */}
                </div>
                <div className="relative">
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t('descriptionPlaceholder')}
                    className="h-28 resize-none bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-black focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                  <p className="text-sm font-medium text-foreground">
                    {t('emailLabel')}
                  </p>
                </div>
                <div className="relative">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="bg-transparent px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:ring-foreground focus-visible:ring-offset-background"
                  />
                </div>
              </div>

              {(error || status === 'success') && (
                <div
                  className={cn(
                    'rounded-xl border px-3 py-2 text-sm',
                    status === 'success'
                      ? 'border-foreground/30 bg-foreground/5 text-foreground'
                      : 'border-destructive/50 bg-destructive/10 text-destructive-foreground',
                  )}
                >
                  {status === 'success' ? t('success') : error || t('error')}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={close}
                  className="text-foreground hover:bg-muted hover:text-foreground"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={disableSubmit}
                  className="bg-foreground text-background shadow-sm shadow-black/10 transition hover:bg-foreground/90 disabled:bg-foreground/60"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-[1.5px] border-background/30 border-t-background" />
                      {t('submitting')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {t('submit')}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
